import { React } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../../Styles/theme';

import { useLanguage } from '../../../Context/LanguageContext';

export default function SalesDaily(props) {
  const {
    date,
    data,
    onClickClose,
    onClickSalesItem
  } = props;

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;
  const { t } = useLanguage();

  // 일별 총 결제 금액 //
  const totalSalesPrice = data.reduce((sum, item) => sum + item.totalPrice, 0);

  // 결제 수단 번역 //
  function translatePaymentOption(name) {
    const map = { '카드': t('statistics.sales.optionCard'), '현금': t('statistics.sales.optionCash'), '포인트': t('statistics.sales.optionPoint'), '링코페이': t('statistics.sales.linkoPay') };
    return map[name] || name;
  }

  // 결제 금액 정리 //
  function decideTotalPriceView(data) {
    if (data.selectedPaymentOptions === undefined || data.selectedPaymentOptions.length === 0) {
      if (data.cardPrice === 0 && data.cashPrice === 0) return t('statistics.sales.free');
      else if (data.cardPrice === 0) return data.cashPrice.toLocaleString('ko-KR') + t('statistics.sales.paymentCash');
      else if (data.cashPrice === 0) return data.cardPrice.toLocaleString('ko-KR') + t('statistics.sales.paymentCard');
      else return (parseInt(data.cashPrice) + parseInt(data.cardPrice)).toLocaleString('ko-KR') + t('statistics.sales.paymentMixed');
    }
    else {
      const allSame = data.selectedPaymentOptions.every(item => item.paymentOptionName === data.selectedPaymentOptions[0].paymentOptionName);
      const totalPrice = data.selectedPaymentOptions.reduce((sum, item) => sum + item.price, 0);

      if (allSame) return totalPrice.toLocaleString('ko-KR') + ' - ' + translatePaymentOption(data.selectedPaymentOptions[0].paymentOptionName);
      else return totalPrice.toLocaleString('ko-KR') + t('statistics.sales.paymentMixed');
    }
  }

  return (
    <TopLevelWrapper>
      <TitleWrapper>
        <TitleTextWrapper>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {date.format(t('statistics.dateFormatDay')) + ' ' + t('statistics.sales.totalSalesHistory') + ' ( ' + data.length + t('statistics.sales.caseSuffix') + ' )'}
          </Text>
          <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>
            {'— ' + totalSalesPrice.toLocaleString('ko-KR') + ' ' + t('statistics.won')}
          </Text>
        </TitleTextWrapper>
        <CloseButton onClick={onClickClose} />
      </TitleWrapper>
      {
        data.length > 0 ?
        <TableWrapper>
          <HeadRow>
            <NameColumn>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.sales.colName')}</Text>
            </NameColumn>
            <HistoryColumn>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.sales.colPaymentHistory')}</Text>
            </HistoryColumn>
            <PriceColumn>
              <Text fontFamily='NotoSansKR-Medium' fontSize={14} color={theme.text}>{t('statistics.sales.colPaymentAmount')}</Text>
            </PriceColumn>
          </HeadRow>
          <Divider />
          <BodyWrapper>
            {
              data.map((item, index) => (
                <BodyRow key={'SalesDaily-' + index} onClick={() => onClickSalesItem(item)}>
                  <NameColumn>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={item.isRefund ? theme.pointRed : theme.text}>
                      {item.name === undefined ? t('statistics.sales.withdrawnMember') : item.name}
                    </Text>
                  </NameColumn>
                  <HistoryColumn>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={item.isRefund ? theme.pointRed : theme.text}>
                      {item.itemName}
                    </Text>
                  </HistoryColumn>
                  <PriceColumn>
                    <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={item.isRefund ? theme.pointRed : theme.text}>
                      {decideTotalPriceView(item)}
                    </Text>
                  </PriceColumn>
                </BodyRow>
              ))
            }
          </BodyWrapper>
        </TableWrapper>
        :
        <EmptyWrapper>
          <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.subText}>{t('statistics.sales.noSalesHistory')}</Text>
        </EmptyWrapper>
      }
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: flex-start;

  min-height: 428px;
  width: 567px;

  padding: 0px 20px;

  gap: 10px;

  border-radius: 10px;
  background-color: ${props => props.theme.subBackground};
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: space-between;
  align-items: center;

  padding: 10px 0px;

  width: 100%;
  height: 30px;
`;

const TitleTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;
`;

const CloseButton = styled.div`
  position: relative;

  width: 20px;
  height: 20px;

  cursor: pointer;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;

    width: 19.412px;
    height: 3px;

    background-color: ${props => props.theme.line};
    border-radius: 300px;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }

  &:hover::before,
  &:hover::after {
    background-color: ${props => props.theme.text};
  }
`;

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  width: 100%;
`;

const HeadRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 0px 10px;

  width: calc(100% - 20px);
  height: 30px;
`;

const EmptyWrapper = styled.div`
  display: flex;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 330px;
`;

const Divider = styled.div`
  width: calc(100% - 20px);
  height: 1px;

  margin: 7px 10px;

  opacity: 0.5;
  background-color: ${props => props.theme.line};
`;

const BodyWrapper = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  max-height: 320px;

  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.theme.scrollBar};
    border-radius: 50px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
    border-radius: 50px;
  }
`;

const BodyRow = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  padding: 10px 10px;

  width: calc(100% - 20px);

  border-radius: 5px;

  transition: all 0.3s ease-in-out;

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.primary};
  }

  &:hover p {
    color: ${props => props.theme.subBackground};
    cursor: pointer;
  }
`;

const NameColumn = styled.div`
  display: flex;
  align-items: center;

  width: 140px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HistoryColumn = styled.div`
  display: flex;
  align-items: center;

  flex: 1;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PriceColumn = styled.div`
  display: flex;
  align-items: center;

  width: 150px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0;

  transition: color 0.3s ease-in-out;

  &:hover {
    cursor: default;
  }
`;
